import { z } from 'zod';
import { nicknameProblem, type NicknameProblem } from './challenge.ts';
import { foldText } from './normalize.ts';

/*
 * «De cirio a hermano mayor»: the contract between the API and the web for accounts and
 * the game (docs/simulator-accounts.md). Texts the player accepts are versioned here, so
 * the proof of consent stores exactly what was shown.
 */

/** Version of the privacy policy and terms (provisional, to be reviewed by a lawyer). */
export const SIM_POLICY_VERSION = '2026-11-provisional';

export const SIM_CONSENT_TEXTS = {
  terms:
    'He leído y acepto las condiciones de uso y la política de privacidad de «De cirio a hermano mayor».',
  marketing:
    'Quiero recibir por correo novedades del juego, de Cruz de IA y del proyecto. Puedo darme de baja cuando quiera.',
} as const;

/** Days a brotherhood can go out, in order (the engine's PROCESSION_DAYS). */
export const SIM_PROCESSION_DAYS = [
  'Viernes de Dolores',
  'Sábado de Pasión',
  'Domingo de Ramos',
  'Lunes Santo',
  'Martes Santo',
  'Miércoles Santo',
  'Jueves Santo',
  'Madrugá',
  'Viernes Santo',
  'Sábado Santo',
  'Domingo de Resurrección',
] as const;

export const SIM_LIMITS = {
  nickname: { min: 3, max: 20 },
  password: { min: 10, max: 128 },
  email: { max: 254 },
} as const;

/** Same nickname for case, accents and spaces: "Paco", "paco" and "Pa có" are one. */
export const simNicknameKey = (nickname: string): string => foldText(nickname).replace(/ /g, '');

/** Emails are unique ignoring case and surrounding spaces. */
export const simEmailKey = (email: string): string => email.trim().toLowerCase();

export type SimNicknameProblem = NicknameProblem;

export function simNicknameProblem(raw: string): SimNicknameProblem | null {
  const nickname = raw.trim().replace(/\s+/g, ' ');
  if (nickname.length < SIM_LIMITS.nickname.min) return 'too_short';
  if (nickname.length > SIM_LIMITS.nickname.max) return 'too_long';
  if (simNicknameKey(nickname).length < SIM_LIMITS.nickname.min) return 'invalid_characters';
  return nicknameProblem(nickname);
}

export const SIM_NICKNAME_MESSAGES: Record<SimNicknameProblem | 'taken', string> = {
  too_short: `El apodo debe tener al menos ${SIM_LIMITS.nickname.min} caracteres.`,
  too_long: `El apodo puede tener como mucho ${SIM_LIMITS.nickname.max} caracteres.`,
  invalid_characters: 'Usa letras, números, espacios o . - _',
  offensive: 'Elige otro apodo, por favor.',
  taken: 'Ese apodo ya lo tiene otro hermano. Prueba con otro.',
};

export type SimPasswordProblem = 'too_short' | 'too_long' | 'common' | 'personal';

export const SIM_PASSWORD_MESSAGES: Record<SimPasswordProblem, string> = {
  too_short: `La contraseña debe tener al menos ${SIM_LIMITS.password.min} caracteres.`,
  too_long: `La contraseña puede tener como mucho ${SIM_LIMITS.password.max} caracteres.`,
  common: 'Esa contraseña es de las más usadas y se adivina enseguida. Elige otra.',
  personal: 'La contraseña no puede contener tu apodo ni tu correo.',
};

const nickname = z
  .string()
  .transform((s) => s.trim().replace(/\s+/g, ' '))
  .superRefine((s, ctx) => {
    const problem = simNicknameProblem(s);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

const email = z
  .string()
  .trim()
  .max(SIM_LIMITS.email.max)
  .pipe(z.email({ message: 'invalid_email' }));

export const simRegisterSchema = z.object({
  email,
  password: z.string().max(SIM_LIMITS.password.max),
  nickname,
  /** The terms must be accepted to play */
  accept_terms: z.literal(true),
  /** Separate, unticked by default: the account works the same without it */
  marketing: z.boolean().default(false),
});
export type SimRegisterRequest = z.input<typeof simRegisterSchema>;

export const simLoginSchema = z.object({
  email: z.string().trim().max(SIM_LIMITS.email.max),
  password: z.string().max(SIM_LIMITS.password.max),
});

export const simTokenSchema = z.object({ token: z.string().min(20).max(200) });
export const simEmailSchema = z.object({ email });
export const simResetSchema = z.object({
  token: z.string().min(20).max(200),
  password: z.string().max(SIM_LIMITS.password.max),
});
export const simPreferencesSchema = z.object({ marketing: z.boolean() });
export const simDeleteSchema = z.object({ password: z.string().max(SIM_LIMITS.password.max) });
export const simUnsubscribeSchema = z.object({ key: z.string().min(20).max(200) });

export const simBrotherhoodSchema = z.object({
  name: z.string().max(200),
  popular_name: z
    .string()
    .max(80)
    .nullish()
    .transform((s) => (s?.trim() ? s.trim() : null)),
  day: z.string(),
  church: z.string().max(120),
});
export type SimBrotherhoodRequest = z.input<typeof simBrotherhoodSchema>;

export const simChoiceSchema = z.object({ choice: z.number().int().min(0).max(2) });

/** The account as the web sees it. Never includes the password or tokens. */
export interface SimMe {
  nickname: string;
  email: string;
  email_verified: boolean;
  marketing: boolean;
  created_at: string;
  career_id: string | null;
}

export type SimErrorCode =
  | 'invalid_request'
  | 'unauthorized'
  | 'csrf'
  | 'email_taken'
  | 'nickname_taken'
  | 'nickname_invalid'
  | 'password_invalid'
  | 'invalid_credentials'
  | 'locked'
  | 'token_invalid'
  | 'brotherhood_invalid'
  | 'career_exists'
  | 'no_career'
  | 'not_waiting'
  | 'too_many'
  | 'not_found';

// ------------------------------------------------------------------------ game views

export type SimStatKey = 'devotion' | 'prestige' | 'board' | 'stamina' | 'wallet';

/** Moments of the career worth a share card. */
export type SimTimelineKind =
  'promotion' | 'achievement' | 'milestone' | 'election' | 'substitution' | 'start' | 'end';

/** A career as the web sees it: no seeds, flags or library ids. */
export interface SimCareerView {
  id: string;
  finished: boolean;
  brotherhood: {
    name: string;
    popular_name: string | null;
    day: string;
    church: string;
    members: number;
    nazarenes: number;
    prestige: number;
    treasury: number;
    milestones: { key: string; label: string }[];
  };
  player: {
    member_number: number;
    seniority_years: number;
    rank: string;
    rank_label: string;
    post: string | null;
    position: number;
    tramo: number | null;
    tramos: number;
    processions: number;
    stats: { key: SimStatKey; label: string; value: number }[];
  };
  time: {
    year: number;
    phase: string;
    phase_label: string;
    streak: number;
    best_streak: number;
    days_played: number;
    mandate_left: number | null;
  };
  today: {
    day: string;
    events_per_day: number;
    events_left: number;
    next_day_at: string | null;
    holy_week: { starts_at: string; on_now: boolean };
  };
  characters: {
    id: string;
    name: string;
    role: string;
    description: string;
    /** −10 … +10 */
    relation: number;
  }[];
  /** The whole catalogue: the ones not unlocked yet show as empty frames */
  achievements: {
    id: string;
    name: string;
    description: string;
    unlocked: boolean;
    year: number | null;
    shareable: boolean;
  }[];
  timeline: {
    /** Position in the career's timeline: the `ref` to share it */
    index: number;
    year: number;
    phase_label: string;
    kind: SimTimelineKind;
    text: string;
    shareable: boolean;
  }[];
}

/** An event to answer: the text and the options, never what each option does. */
export interface SimEventView {
  idx: number;
  /** Yesterday's cliffhanger, when this event resolves it */
  from_yesterday: string | null;
  title: string;
  text: string;
  options: { index: number; text: string }[];
  last_of_day: boolean;
}

/** What a choice changed, ready to show. */
export interface SimChangesView {
  stats: { key: SimStatKey; label: string; delta: number }[];
  relation: { name: string; delta: number } | null;
  brotherhood: { members: number; prestige: number; treasury: number };
  promoted: { rank_label: string; post: string | null } | null;
  achievements: { id: string; name: string; description: string; shareable: boolean }[];
  year_ended: boolean;
}

export interface SimEventResponse {
  event: SimEventView | null;
  career: SimCareerView;
}

export interface SimChoiceResponse {
  outcome: string;
  changes: SimChangesView;
  /** The cliffhanger left for tomorrow, on the last event of the day */
  hook: string | null;
  day_done: boolean;
  career: SimCareerView;
}

export interface SimBrotherhoodProblem {
  field: 'name' | 'popular_name' | 'day' | 'church' | 'all';
  reason: string;
  message: string;
}

export interface SimBrotherhoodCheck {
  ok: boolean;
  problems: SimBrotherhoodProblem[];
}

// --------------------------------------------------------------------------- shares

export const simShareSchema = z.object({
  kind: z.enum(['achievement', 'moment']),
  /** The achievement id, or the timeline index of the moment */
  ref: z.string().min(1).max(60),
});
export type SimShareRequest = z.infer<typeof simShareSchema>;

export const SIM_SHARE_ID = /^[a-z2-9]{10}$/;

/** A share card: a frozen snapshot, with nothing personal but the nickname. */
export interface SimShareCard {
  id: string;
  nickname: string;
  /** Popular name, or the full name when there is none */
  brotherhood: string;
  rank_label: string;
  seniority_years: number;
  member_number: number;
  /** Small line above the title: "Logro", "Ascenso" */
  kicker: string;
  /** "Fiscal de paso", "Estreno de túnica" */
  title: string;
  /** In first person: "Me han nombrado fiscal de paso tras 18 años de hermano." */
  phrase: string;
  created_at: string;
}

export interface SimShareResponse {
  card: SimShareCard;
  /** Public page of the card (/simulador/logro/:id) */
  url: string;
  /** 1080 × 1920, for WhatsApp and Instagram stories */
  image_story: string;
  /** 1200 × 630, for link previews */
  image_wide: string;
}

// -------------------------------------------------------------------------- reports

export const SIM_REPORT_REASONS = {
  offensive: 'Es ofensivo o de mal gusto',
  faith: 'Falta al respeto a la fe',
  real: 'Habla de personas o hermandades reales',
  nonsense: 'No tiene sentido o tiene errores',
  other: 'Otra cosa',
} as const;
export type SimReportReason = keyof typeof SIM_REPORT_REASONS;

const reportFields = {
  reason: z.enum(Object.keys(SIM_REPORT_REASONS) as [SimReportReason, ...SimReportReason[]]),
  comment: z
    .string()
    .max(500)
    .nullish()
    .transform((s) => (s?.trim() ? s.trim() : null)),
};

/** A player reports one of their events (by its index in the career). */
export const simEventReportSchema = z.object({ idx: z.number().int().min(0), ...reportFields });
/** Anyone reports a shared card. */
export const simShareReportSchema = z.object(reportFields);

// ------------------------------------------------------------- review panel

export interface SimSwitches {
  /** Any AI at all (live events, review, diary) */
  ai: boolean;
  /** Events written live (off: library only, review and diary still on) */
  ai_live: boolean;
  /** Euros a day for AI before playing from the library only */
  daily_budget_eur: number;
  /** Reports from different players that retire a library event or hide a card */
  report_threshold: number;
}

export interface SimSwitchesView extends SimSwitches {
  /** Forced off by the environment: the panel cannot change them */
  locked: { ai: boolean; ai_live: boolean };
}

/** One thing reported, with all its open reports (what the panel shows). */
export interface SimReportGroup {
  key: string;
  target: 'event' | 'share';
  /** Library event: retiring it removes it for everyone */
  library_id: string | null;
  share_id: string | null;
  /** The library event is retired / the card is hidden now */
  removed: boolean;
  snapshot: unknown;
  reports: { reason: string; comment: string | null; created_at: string; signed_in: boolean }[];
  first_at: string;
}

export interface SimDayMetrics {
  day: string;
  signups: number;
  active_players: number;
  events: number;
  events_per_player: number;
  ai_cost_eur: number;
  cost_per_player_eur: number;
  reviews: number;
  rejected: number;
  rejection_rate: number;
  reports: number;
  shares: number;
}

export interface SimRetention {
  /** Players whose day N after signing up is already over */
  eligible: number;
  /** Of them, those who played on that day */
  retained: number;
  rate: number;
}

export interface SimMetrics {
  today: string;
  days: SimDayMetrics[];
  retention: { d1: SimRetention; d7: SimRetention; d30: SimRetention };
  totals: {
    users: number;
    careers: number;
    finished_careers: number;
    marketing_rate: number;
    shares: number;
    open_reports: number;
    library_active: number;
    library_retired: number;
    ai_cost_eur: number;
  };
}
